import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Button, ProgressBar, Modal, Row, Col, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faTrash, faBan, faChartBar } from '@fortawesome/free-solid-svg-icons';
import c2Service from '../../services/c2Service';

const JobsList = ({ wsRef }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedJob, setSelectedJob] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadJobs();
    loadStats();
  }, [filter]);

  // Listen for WebSocket real-time job updates
  useEffect(() => {
    if (wsRef?.current) {
      const originalOnMessage = wsRef.current.onmessage;

      wsRef.current.onmessage = (event) => {
        const message = JSON.parse(event.data);

        // Handle job updates
        if (message.type === 'job_update') {
          console.log('[JobsList] Real-time update:', message);
          // Update job in list
          setJobs(prevJobs => {
            return prevJobs.map(job => {
              if (job.bullJobId === message.jobId) {
                return {
                  ...job,
                  status: message.status,
                  progress: message.data?.progress || job.progress
                };
              }
              return job;
            });
          });
        }

        // Call original handler
        if (originalOnMessage) {
          originalOnMessage(event);
        }
      };
    }
  }, [wsRef]);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filter !== 'all') {
        filters.status = filter;
      }
      const response = await c2Service.getJobs(filters);
      setJobs(response.data?.data || []);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await c2Service.getJobStats();
      setStats(response.data?.data || null);
    } catch (error) {
      console.error('Failed to load job stats:', error);
    }
  };

  const handleCancelJob = async (jobId) => {
    if (!window.confirm('Cancel this job?')) return;

    try {
      await c2Service.cancelJob(jobId);
      loadJobs();
    } catch (error) {
      console.error('Failed to cancel job:', error);
      alert('Failed to cancel job: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteJob = async (jobId) => {
    if (!window.confirm('Delete this job?')) return;

    try {
      await c2Service.deleteJob(jobId);
      loadJobs();
      if (showDetailModal) {
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error('Failed to delete job:', error);
      alert('Failed to delete job: ' + (error.response?.data?.error || error.message));
    }
  };

  const openDetailModal = async (job) => {
    try {
      const response = await c2Service.getJob(job._id);
      setSelectedJob(response.data?.data || job);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Failed to load job details:', error);
      setSelectedJob(job);
      setShowDetailModal(true);
    }
  };

  const getStatusBadge = (job) => {
    const statusConfig = {
      queued: { bg: 'secondary', text: 'Queued' },
      waiting: { bg: 'info', text: 'Waiting' },
      active: { bg: 'primary', text: 'Active' },
      running: { bg: 'primary', text: 'Running' },
      completed: { bg: 'success', text: 'Completed' },
      failed: { bg: 'danger', text: 'Failed' },
      cancelled: { bg: 'dark', text: 'Cancelled' }
    };
    const config = statusConfig[job.status] || statusConfig.queued;
    return <Badge bg={config.bg}>{config.text}</Badge>;
  };

  const getJobTypeLabel = (jobType) => {
    const labels = {
      port_scan: 'Port Scan',
      keylog: 'Keylogger',
      process_monitor: 'Process Monitor',
      file_search: 'File Search',
      screenshot_batch: 'Screenshot Batch',
      network_discovery: 'Network Discovery'
    };
    return labels[jobType] || jobType;
  };

  const formatDuration = (startedAt, completedAt) => {
    if (!startedAt) return '-';
    const end = completedAt || new Date();
    const duration = new Date(end) - new Date(startedAt);
    const seconds = Math.floor(duration / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const filteredJobs = jobs.filter(job => {
    if (filter === 'all') return true;
    if (filter === 'active') return ['queued', 'waiting', 'active', 'running'].includes(job.status);
    return job.status === filter;
  });

  return (
    <>
      <Card>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem 1rem', backgroundColor: '#374151', borderBottom: '1px solid #4b5563', flexWrap: 'wrap', gap: '1rem' }}>
          <strong style={{ color: '#fff' }}>Background Jobs ({filteredJobs.length})</strong>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '0' }}>
              <button onClick={() => setFilter('all')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'all' ? '#3b82f6' : 'transparent', color: filter === 'all' ? '#fff' : '#3b82f6', border: '1px solid #3b82f6', borderRight: 'none', cursor: 'pointer', borderTopLeftRadius: '0.375rem', borderBottomLeftRadius: '0.375rem' }}>All</button>
              <button onClick={() => setFilter('active')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'active' ? '#3b82f6' : 'transparent', color: filter === 'active' ? '#fff' : '#3b82f6', border: '1px solid #3b82f6', borderRight: 'none', cursor: 'pointer' }}>Active</button>
              <button onClick={() => setFilter('completed')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'completed' ? '#10b981' : 'transparent', color: filter === 'completed' ? '#fff' : '#10b981', border: '1px solid #10b981', borderRight: 'none', cursor: 'pointer' }}>Completed</button>
              <button onClick={() => setFilter('failed')} style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: filter === 'failed' ? '#ef4444' : 'transparent', color: filter === 'failed' ? '#fff' : '#ef4444', border: '1px solid #ef4444', cursor: 'pointer', borderTopRightRadius: '0.375rem', borderBottomRightRadius: '0.375rem' }}>Failed</button>
            </div>
            <button
              onClick={loadJobs}
              style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem', backgroundColor: '#6b7280', color: '#fff', border: '1px solid #6b7280', borderRadius: '0.375rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '0.375rem' }}
            >
              <FontAwesomeIcon icon={faSync} />
              Refresh
            </button>
          </div>
        </div>

        <Table striped hover variant="dark" className="mb-0">
          <thead>
            <tr>
              <th>Status</th>
              <th>Job Type</th>
              <th>Beacon</th>
              <th>Progress</th>
              <th>Started</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredJobs.map(job => (
              <tr key={job._id} style={{ cursor: 'pointer' }} onClick={() => openDetailModal(job)}>
                <td>{getStatusBadge(job)}</td>
                <td>
                  <strong>{getJobTypeLabel(job.jobType)}</strong>
                </td>
                <td>
                  <code className="small">{job.beacon?.systemInfo?.hostname || job.beacon?.beaconId || 'N/A'}</code>
                </td>
                <td>
                  {['active', 'running'].includes(job.status) && job.progress > 0 ? (
                    <div style={{ minWidth: '100px' }}>
                      <ProgressBar now={job.progress} label={`${job.progress}%`} variant="info" />
                    </div>
                  ) : (
                    <span style={{ color: '#6b7280' }}>—</span>
                  )}
                </td>
                <td>
                  <small style={{ color: '#9ca3af' }}>
                    {job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}
                  </small>
                </td>
                <td>
                  <small style={{ color: '#9ca3af' }}>
                    {formatDuration(job.startedAt, job.completedAt)}
                  </small>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    {['queued', 'waiting', 'active', 'running'].includes(job.status) && (
                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() => handleCancelJob(job._id)}
                        title="Cancel job"
                      >
                        <FontAwesomeIcon icon={faBan} />
                      </Button>
                    )}
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleDeleteJob(job._id)}
                      title="Delete job"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {filteredJobs.length === 0 && (
          <div style={{ textAlign: 'center', color: '#9ca3af', padding: '3rem 0' }}>
            <p>No jobs found</p>
            <small>Background jobs will appear here</small>
          </div>
        )}
      </Card>

      {/* Job Stats Card */}
      {stats && (
        <Card className="mt-3">
          <Card.Header style={{ backgroundColor: '#374151', borderColor: '#4b5563', color: '#fff' }}>
            <FontAwesomeIcon icon={faChartBar} className="me-2" />
            Statistics
          </Card.Header>
          <Card.Body style={{ backgroundColor: '#1f2937' }}>
            <Row>
              <Col md={3}>
                <div className="text-center mb-3">
                  <h3 style={{ color: '#3b82f6' }}>{stats.total}</h3>
                  <small className="text-muted">Total Jobs</small>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center mb-3">
                  <h3 style={{ color: '#10b981' }}>{stats.byStatus?.completed || 0}</h3>
                  <small className="text-muted">Completed</small>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center mb-3">
                  <h3 style={{ color: '#ef4444' }}>{stats.byStatus?.failed || 0}</h3>
                  <small className="text-muted">Failed</small>
                </div>
              </Col>
              <Col md={3}>
                <div className="text-center mb-3">
                  <h3 style={{ color: '#6b7280' }}>{stats.successRate}%</h3>
                  <small className="text-muted">Success Rate</small>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Job Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: '#1f2937', borderColor: '#374151' }}>
          <Modal.Title style={{ color: '#e5e7eb' }}>Job Details</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: '#111827' }}>
          {selectedJob && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#e5e7eb' }}>Status:</strong> {getStatusBadge(selectedJob)}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#e5e7eb' }}>Type:</strong> {getJobTypeLabel(selectedJob.jobType)}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#e5e7eb' }}>Beacon:</strong> {selectedJob.beacon?.systemInfo?.hostname || selectedJob.beacon?.beaconId || 'N/A'}
                  </div>
                </Col>
                <Col md={6}>
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#e5e7eb' }}>Progress:</strong> {selectedJob.progress}%
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#e5e7eb' }}>Started:</strong> {selectedJob.startedAt ? new Date(selectedJob.startedAt).toLocaleString() : 'Not started'}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    <strong style={{ color: '#e5e7eb' }}>Duration:</strong> {formatDuration(selectedJob.startedAt, selectedJob.completedAt)}
                  </div>
                </Col>
              </Row>

              {selectedJob.params && Object.keys(selectedJob.params).length > 0 && (
                <div className="mb-3">
                  <strong style={{ color: '#e5e7eb', display: 'block', marginBottom: '0.5rem' }}>Parameters:</strong>
                  <pre style={{ backgroundColor: '#0d1117', color: '#e5e7eb', padding: '1rem', borderRadius: '0.375rem', fontSize: '0.875rem', maxHeight: '200px', overflow: 'auto' }}>
                    {JSON.stringify(selectedJob.params, null, 2)}
                  </pre>
                </div>
              )}

              {selectedJob.result && (
                <div className="mb-3">
                  <strong style={{ color: '#e5e7eb', display: 'block', marginBottom: '0.5rem' }}>Result:</strong>
                  <pre style={{ backgroundColor: '#0d1117', color: '#e5e7eb', padding: '1rem', borderRadius: '0.375rem', fontSize: '0.875rem', maxHeight: '300px', overflow: 'auto' }}>
                    {typeof selectedJob.result === 'string' ? selectedJob.result : JSON.stringify(selectedJob.result, null, 2)}
                  </pre>
                </div>
              )}

              {selectedJob.error && (
                <div className="mb-3">
                  <strong style={{ color: '#ef4444', display: 'block', marginBottom: '0.5rem' }}>Error:</strong>
                  <pre style={{ backgroundColor: '#1f2937', color: '#ef4444', padding: '1rem', borderRadius: '0.375rem', fontSize: '0.875rem', border: '1px solid #ef4444' }}>
                    {selectedJob.error}
                  </pre>
                </div>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: '#1f2937', borderColor: '#374151' }}>
          {selectedJob && ['queued', 'waiting', 'active', 'running'].includes(selectedJob.status) && (
            <Button variant="warning" onClick={() => handleCancelJob(selectedJob._id)}>
              <FontAwesomeIcon icon={faBan} /> Cancel Job
            </Button>
          )}
          <Button variant="danger" onClick={() => selectedJob && handleDeleteJob(selectedJob._id)}>
            <FontAwesomeIcon icon={faTrash} /> Delete
          </Button>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default JobsList;
